<template>
  <div class="container py-3">
    <!-- Header -->
    <h2 class="fs-4 fw-bold mb-3 text-center">Job Management</h2>

    <!-- Loading / Error -->
    <div v-if="loadingJob" class="alert alert-info text-center">Loading job details...</div>
    <div v-else-if="!currentJob" class="alert alert-danger text-center">
      Job not found or unauthorized
    </div>

    <!-- Job Card -->
    <div v-else class="card shadow-sm border-0 rounded-3 p-3">
      <div class="card-body">
        <!-- Job Info -->
        <div class="mb-3">
          <label class="form-label">Job ID</label>
          <input
            type="text"
            class="form-control form-control-lg"
            :value="currentJob.jobId"
            disabled
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Title</label>
          <input
            type="text"
            class="form-control form-control-lg"
            :value="currentJob.title"
            disabled
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Client</label>
          <input
            type="text"
            class="form-control form-control-lg"
            :value="currentJob.client"
            disabled
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Start Location</label>
          <input
            type="text"
            class="form-control form-control-lg"
            :value="currentJob.startLocation"
            disabled
          />
        </div>

        <div class="mb-3">
          <label class="form-label">End Location</label>
          <input
            type="text"
            class="form-control form-control-lg"
            :value="currentJob.endLocation"
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Job Date</label>
          <input
            type="text"
            class="form-control form-control-lg"
            :value="currentJob.jobDate"
            disabled
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Job Time</label>
          <input
            type="text"
            class="form-control form-control-lg"
            :value="currentJob.jobTime"
            disabled
          />
        </div>


        <!-- Buttons -->
        <div class="d-flex gap-2">
          <button
            v-if="currentJob.status === 'assigned'"
            class="btn btn-success flex-fill py-2"
            @click="startJob"
          >
            Start Job
          </button>
          <button
            v-if="currentJob.status === 'active'"
            class="btn btn-danger flex-fill py-2"
            @click="endJob"
          >
            End Job
          </button>
        </div>
      </div>
    </div>

    <!-- Login Modal -->
    <div
      v-if="showLogin"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5);"
    >
      <div class="modal-dialog modal-dialog-centered" style="max-width: 400px;">
        <div class="modal-content rounded-3 shadow">
          <div class="modal-header border-0">
            <h5 class="modal-title fw-bold">Login Required</h5>
            <button type="button" class="btn-close" @click="showLogin = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Email</label>
              <input type="email" v-model="loginEmail" class="form-control form-control-lg" />
            </div>
            <div class="mb-3">
              <label class="form-label">Password</label>
              <input
                type="password"
                v-model="loginPassword"
                class="form-control form-control-lg"
              />
            </div>
          </div>
          <div class="modal-footer border-0">
            <button class="btn btn-secondary w-50" @click="showLogin = false">Cancel</button>
            <button class="btn btn-primary w-50" @click="handleLogin" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              Login
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  signInWithEmailAndPassword,
} from "firebase/auth";
import axios from "axios";
import { useToast } from "vue-toastification";

export default {
  name: "StartEndJob",
  data() {
    return {
      // apiURL: "http://127.0.0.1:5001",
      apiURL: "https://genstoryflow-backend-main-build-353838813175.europe-west1.run.app",
      currentJob: null,
      note: "",
      loginEmail: "",
      loginPassword: "",
      loading: false,
      loadingJob: true,
      showLogin: false,
    };
  },
  mounted() {
    this.initPage();
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  methods: {
    getStoredToken() {
      const token = localStorage.getItem("exactflosuite_authToken");
      const expiry = localStorage.getItem("exactflosuite_authExpiry");

      if (!token || !expiry) return null;
      if (Date.now() > parseInt(expiry)) {
        localStorage.removeItem("exactflosuite_authToken");
        localStorage.removeItem("exactflosuite_authExpiry");
        return null;
      }
      return token;
    },

    async initPage() {
      const token = this.getStoredToken();
      if (token) {
        await this.fetchJob(token);
      } else {
        this.loadingJob = false;
        this.showLogin = true;
      }
    },

    async fetchJob(token) {
      this.loadingJob = true;
      try {
        const res = await axios.get(`${this.apiURL}/api/jobs/${this.$route.params.id}`, {
          headers: { Authorization: `${token}` },
        });
        this.currentJob = res.data.data;
        console.log(this.currentJob)
      } catch (err) {
        console.error(err);
        this.currentJob = null;
      } finally {
        this.loadingJob = false;
      }
    },

    async handleLogin() {
      if (!this.loginEmail || !this.loginPassword) {
        alert("Please enter email and password");
        return;
      }
      this.loading = true;
      try {
        const auth = getAuth();
        await setPersistence(auth, browserLocalPersistence);
        const userCredential = await signInWithEmailAndPassword(
          auth,
          this.loginEmail,
          this.loginPassword
        );

        const token = await userCredential.user.getIdToken();
        const payload = JSON.parse(atob(token.split(".")[1]));
        const expiry = payload.exp * 1000;

        localStorage.setItem("exactflosuite_authToken", token);
        localStorage.setItem("exactflosuite_authExpiry", expiry);

        this.showLogin = false;
        this.loginEmail = "";
        this.loginPassword = "";

        await this.fetchJob(token);
      } catch (err) {
        alert("Login failed: " + err.message);
      } finally {
        this.loading = false;
      }
    },

    async startJob() {
      const token = this.getStoredToken();
      if (!token) {
        this.showLogin = true;
        return;
      }

      try {
        await axios.post(`${this.apiURL}/api/jobs/${this.currentJob.id}/start`, {}, {
          headers: { Authorization: token },
        });
        this.toast.success("Job started successfully");
        await this.fetchJob(token);
      } catch (err) {
        console.error("Error starting job:", err);
        this.toast.error("Failed to start job");
      }
    },

    async endJob() {
      const token = this.getStoredToken();
      if (!token) {
        this.showLogin = true;
        return;
      }

      // Validate end location
      if (!this.currentJob.endLocation || this.currentJob.endLocation.trim() === "") {
        this.toast.error("Please provide an end location before ending the job");
        return;
      }

      try {
        const payload = {
          endLocation: this.currentJob.endLocation, // Pass directly to backend
        };

        await axios.post(`${this.apiURL}/api/jobs/${this.currentJob.id}/end`, payload, {
          headers: { Authorization: token },
        });

        this.toast.success("Job ended successfully");
        await this.fetchJob(token);
      } catch (err) {
        console.error("Error ending job:", err);
        this.toast.error("Failed to end job");
      }
    }

  },
};
</script>
